import { DEFAULT_PITCH, INSTRUMENT_LABELS, SECTION_LABELS } from '../../domain/factories';
import { OpError, addSection, buildupFrom } from '../../domain/projectOps';
import {
  DRUM_INSTRUMENTS,
  MAX_PATTERNS,
  type GeneratorParams,
  type Instrument,
  type NoteEvent,
  type Pattern,
  type Project,
  type SectionKind,
} from '../../domain/schemas';
import { createId } from '../../lib/ids';
import { keyLabel } from '../theory';
import type { GenerateSpec } from '../../prompt/commands';
import type { Composition } from './composer';
import { defaultParams } from './composer';
import { GENRES } from './genres';

/** Fills a partial spec with genre defaults; the seed is supplied when missing. */
export function resolveSpec(spec: GenerateSpec, seed: () => number): GeneratorParams {
  const defined = Object.fromEntries(Object.entries(spec).filter(([, v]) => v !== undefined)) as Partial<GeneratorParams>;
  return defaultParams(spec.genre, { ...defined, seed: spec.seed ?? seed(), focus: spec.focus ?? {} });
}

/** Replaces the project's music with a composition (tracks keep their ids). */
export function applyComposition(project: Project, c: Composition): Project {
  const main = c.patterns.find((p) => p.id.endsWith('drop')) ?? c.patterns.find((p) => p.id.endsWith('verse')) ?? c.patterns[0]!;
  return {
    ...project,
    settings: { ...project.settings, bpm: c.settings.bpm, swing: c.settings.swing, key: c.settings.key },
    tracks: c.tracks,
    patterns: c.patterns,
    activePatternId: main.id,
    arrangement: { sections: c.sections },
    generator: c.params,
    transport: { ...project.transport, mode: 'song' },
  };
}

const pct = (v: number) => `${Math.round(v * 100)}%`;

/** Human-readable, complete description of the parameters (so results are reproducible). */
export function describeComposition(c: Composition): string {
  const p = c.params;
  const focus = Object.entries(p.focus).map(([i, level]) => `${INSTRUMENT_LABELS[i as Instrument]} ${level}`);
  return [
    `Composed ${GENRES[p.genre].label} with the local procedural composer (rule-based, not AI):`,
    `${p.bpm} BPM · ${keyLabel(p.key.root, p.key.scale)} · mood ${p.mood} · intensity ${pct(p.intensity)} · complexity ${pct(p.complexity)}`,
    `${c.sections.length} sections, ${c.bars} bars ≈ ${Math.round(c.durationSec)} s (requested ${p.durationSec} s) · seed ${p.seed}${focus.length ? ` · ${focus.join(', ')}` : ''}.`,
    `The same parameters and seed reproduce this exactly.`,
  ].join(' ');
}

/** Drums of a composition, fitted to a pattern of the given length (for "randomize drums"). */
export function drumsFrom(c: Composition, lengthSteps: number): Partial<Record<Instrument, NoteEvent[]>> {
  const source = c.patterns.find((p) => p.id.endsWith('drop')) ?? c.patterns.find((p) => p.id.endsWith('verse')) ?? c.patterns[0]!;
  const out: Partial<Record<Instrument, NoteEvent[]>> = {};
  for (const instrument of DRUM_INSTRUMENTS) out[instrument] = (source.tracks[instrument] ?? []).filter((n) => n.step < lengthSteps);
  return out;
}

/** A thinned-out version of a pattern for intros, breakdowns and outros (no kick, 808 or backbeat). */
export function strippedFrom(source: Pattern, kind: SectionKind): Pattern {
  const tracks: Pattern['tracks'] = {};
  for (const [instrument, notes] of Object.entries(source.tracks) as [Instrument, NoteEvent[]][]) {
    if (['kick', 'bass808', 'snare', 'clap', 'perc'].includes(instrument)) continue;
    tracks[instrument] = instrument === 'hats' ? notes.filter((n) => n.step % 4 === 0).map((n) => ({ ...n, velocity: Math.max(0.05, n.velocity * 0.7) })) : notes;
  }
  if (!Object.keys(tracks).length) tracks.hats = [{ step: 0, pitch: DEFAULT_PITCH.hats, velocity: 0.4, length: 1, offset: 0 }];
  return { id: createId('pat'), name: SECTION_LABELS[kind], lengthSteps: source.lengthSteps, tracks };
}

/**
 * Inserts a section at `index`. Pattern choice: reuse an existing section of the same kind;
 * otherwise derive one from `reference` (buildup → snare-roll buildup, intro/breakdown/
 * outro → stripped-down variant, verse/drop → the reference pattern itself).
 */
export function insertSectionAt(project: Project, kind: SectionKind, index: number, reference: Pattern): { project: Project; detail: string } {
  const sameKind = project.arrangement.sections.find((s) => s.kind === kind);
  if (sameKind) {
    return { project: addSection(project, kind, sameKind.patternId, sameKind.bars, index), detail: `reusing the existing ${SECTION_LABELS[kind].toLowerCase()} pattern` };
  }
  if (kind === 'verse' || kind === 'drop') {
    return { project: addSection(project, kind, reference.id, 4, index), detail: `using the "${reference.name}" pattern` };
  }
  if (project.patterns.length >= MAX_PATTERNS) throw new OpError(`A project can hold at most ${MAX_PATTERNS} patterns.`);
  const pattern = kind === 'buildup' ? buildupFrom(reference) : strippedFrom(reference, kind);
  const next = { ...project, patterns: [...project.patterns, pattern] };
  return {
    project: addSection(next, kind, pattern.id, 4, index),
    detail: kind === 'buildup' ? 'with a new snare-roll buildup pattern (no kick or 808)' : `with a new stripped-down pattern derived from "${reference.name}"`,
  };
}
